import React from "react";
import { Dimensions } from "react-native";
import { injectIntl } from "react-intl";
import styled, { withTheme, ThemeProvider } from "styled-components/native";
import { material } from "react-native-typography";
import { localize } from "cci-utility";
import Modal from "../panel/Modal";
import DismissKeyboard from "../view/DismissKeyboard";

// Style for title that appears above the dialog
const DialogTitle = styled.Text`
  ${material.display1Object}

  color: ${props => props.theme.fontColor};
  margin-bottom: 15px;
`;

// Dialog itself where the content is, this has the background and rounded corners
const Content = styled.View`
  background-color: ${props => props.theme.dialogBackgroundColor};
  padding: ${props => props.paddings};
  align-items: stretch;
  border-radius: 8px;
  overflow: hidden;
`;

/**
 * Same as the PopupDialog component with the exception that the dialog is NOT rendered until the dialog is shown.
 * 
 * We do this to prevent the children components of a Dialog to be rendered until it is shown, this prevents Queries from reaching the server (in case the dialog uses Apollo), until the dialog is shown.
 * 
 * Props:
 * - title: Object containing intl object or string that is displayed as the dialog title.
 * - autoSnap: Indicates if the dialog should snap to the majority of the screen when on a mobile device. Defaults to true.
 * - autoSnapMode: Indicates if autoSnap applies to x, y or xy axis. Defaults to xy.
 * - width: Width of Dialog, you can use fixed width or use percentage. For example 0.5 it means 50%.
 * - height: Height of Dialog, you can use fixed height or use percentage. For example 0.5 it means 50%.
 * - paddings: Padding for the dialog content, defaults to 25px.
 */
class Dialog extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = { shouldRender: props.isVisible };
  }

  static getDerivedStateFromProps(props, state) {
    if (state.shouldRender !== true) {
      // We render only the first time the dialog is made visible
      return { shouldRender: props.isVisible };
    }

    return null;
  }

  render() {
    const {
        intl,
        theme,
        autoSnap,
        autoSnapMode,
        width: dialogWidth,
        height: dialogHeight,
        paddings,
        children,
        isVisible,
        style,
        title,
        ...restProps
      } = this.props,
      { shouldRender } = this.state,
      { width: screenWidth, height: screenHeight } = Dimensions.get("window"),
      dialog = theme.dialog || {};

    let width = dialogWidth,
      height = dialogHeight;

    // Determine width & height if percentage was specified
    if (width && width > 0.0 && width <= 1.0) {
      width *= screenWidth;
    }
    if (height && height > 0.0 && height <= 1.0) {
      height *= screenHeight;
    }

    if (autoSnap === true) {
      // 411 is sm, large phones breakpoint
      if (screenWidth <= 411) {
        if (autoSnapMode === "xy" || autoSnapMode === "x")
          width = screenWidth * 0.9;
        if (autoSnapMode === "xy" || autoSnapMode === "y")
          height = screenHeight * 0.7;
      }
      // 768 is lg, tablets breakpoint
      else if (screenWidth <= 768) {
        if (autoSnapMode === "xy" || autoSnapMode === "x")
          width = screenWidth * 0.7;
        if (autoSnapMode === "xy" || autoSnapMode === "y")
          height = screenHeight * 0.5;
      }
      // > 768 desktop screens
      else {
        if (autoSnapMode === "xy" || autoSnapMode === "x")
          width = screenWidth * 0.5;
        if (autoSnapMode === "xy" || autoSnapMode === "y")
          height = screenHeight * 0.5;
      }
    }

    return (
      <ThemeProvider theme={dialog}>
        <Modal
          {...restProps}
          isVisible={isVisible}
          style={{ alignItems: "center" }}
          avoidKeyboard={true}
          backdropColor={theme.overlayColor}
        >
          <DismissKeyboard>
            {shouldRender ? (
              <Content style={[{ width, height }, style]} paddings={paddings}>
                {title && <DialogTitle>{localize(intl, title)}</DialogTitle>}

                {children}
              </Content>
            ) : (
              <React.Fragment></React.Fragment>
            )}
          </DismissKeyboard>
        </Modal>
      </ThemeProvider>
    );
  }
}

Dialog.defaultProps = {
  autoSnap: true,
  autoSnapMode: "xy",
  paddings: "25px"
};

export default injectIntl(withTheme(Dialog));
